import React, { useEffect, useState } from "react";
import { Classes, Dialog, InputGroup } from "@blueprintjs/core";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { roleLabels, UnitBenchmarks, UnitRole } from "../models/calc";
import styles from "./UnitEconomicsPage.module.scss";

const roles: { role: UnitRole; hint: string }[] = [
    { role: "production", hint: "производственные должности" },
    { role: "gip", hint: "отдел главных инженеров проектов" },
    { role: "nonproduction", hint: "остальные непроизводственные должности" },
];

/** Целевая загрузка проектами по ролям */
export const BenchmarksDialog = ({ isOpen, benchmarks, onClose, onSave }: BenchmarksDialogProps) => {
    const [values, setValues] = useState<Record<UnitRole, string>>(toStrings(benchmarks));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setValues(toStrings(benchmarks));
        }
    }, [isOpen, benchmarks]);

    const parsed = roles.map(({ role }) => Number(values[role].trim().replace(",", ".")));
    const valid = roles.every(({ role }, i) => values[role].trim() !== "" && parsed[i] >= 0 && parsed[i] <= 100);

    const save = async () => {
        setSaving(true);
        const ok = await onSave({ production: parsed[0], gip: parsed[1], nonProduction: parsed[2] });
        setSaving(false);
        if (ok) {
            onClose();
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title="Целевая загрузка проектами"
            backdropClassName="standard"
            className={`figma-dialog ${styles.benchmarksDialog}`}
        >
            <div className={Classes.DIALOG_BODY}>
                <p className={styles.dialogNote}>
                    Доля доступных часов, которую сотрудник роли должен проводить на проектах. Доступные часы — норма за
                    вычетом отпуска, больничного и прочих уважительных отсутствий.
                </p>
                {roles.map(({ role, hint }) => (
                    <label key={role} className={styles.benchmarkInput}>
                        <span>
                            <span className={styles.benchmarkRole}>{roleLabels[role]}</span>
                            <span className={styles.kpiNote}>{hint}</span>
                        </span>
                        <InputGroup
                            className="planr-default-input"
                            value={values[role]}
                            onChange={(e) => setValues({ ...values, [role]: e.target.value })}
                            rightElement={<span className={styles.inputSuffix}>%</span>}
                            inputMode="decimal"
                        />
                    </label>
                ))}
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <PlanrButton type="greenish" onClick={save} disabled={!valid || saving} loading={saving}>
                        Сохранить
                    </PlanrButton>
                    <PlanrButton type="graybtn" onClick={onClose}>
                        Отмена
                    </PlanrButton>
                </div>
            </div>
        </Dialog>
    );
};

const toStrings = (b: UnitBenchmarks): Record<UnitRole, string> => ({
    production: String(b.production),
    gip: String(b.gip),
    nonproduction: String(b.nonProduction),
});

interface BenchmarksDialogProps {
    isOpen: boolean;
    benchmarks: UnitBenchmarks;
    onClose: () => void;
    onSave: (benchmarks: UnitBenchmarks) => Promise<boolean>;
}
